import React, {StrictMode, useEffect, useState} from 'react';
import ReactDOM from "react-dom/client";
import useFetchData from './fetcher';
import Search from './filter-search';
import Filters from './filters';
import SortList from './sort-list';
import Pagination from './pagination';
import { useMediaQuery } from 'react-responsive';
import GTMDataLayer from './gtm';
import LoadingAnimation from './loading-animation';

import './scss/compatibility.scss';


const CompatibilityApp = () => {
  const fetchUrl = '/rest-api/cp-compatibility-data';
  const [fetch, setFetch] = useState(fetchUrl);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({search: '', type: 'All', comp: 'All', kw: ''});
  const [isReady, setIsReady] = useState(false);
  const [data, setData] = useState([]);
  const [dataCompatibility, setDataCompatibility] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortList, setSortList] = useState('make');
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  useEffect(() => {
    setIsReady(true);

    GTMDataLayer({
      event: 'compatibility_start',
      pageUrl: location.href,
      filters: 'startFilters'
    });
  }, []);

  useEffect(() => {
    setIsReady(true);
    // Fetch data from the rest resource.
    if (isReady) {
      useFetchData(fetch, setData, setLoading);
    }
  }, [isReady]);

  useEffect(() => {
    // Sort by make first.
    if (data.compatibility) {
      setDataCompatibility(data.compatibility.sort((a, b) => a.make.localeCompare(b.make)));
      filters.kw = [0, data.filters.max_kw];
    }
  }, [data]);

  useEffect(() => {
    if (isReady) {
      // Add search to filters.
      setFilters({...filters, search: search});

      // Track search.
      if (search) {
        GTMDataLayer({
          event: 'compatibility_search',
          pageUrl: location.href,
          search: search
        });
      }
    }
  }, [search]);

  // Filter the data.
  useEffect(() => {
    let filteredData = data.compatibility;
    if (data.compatibility) {
      
      // Filter the data based on Type.
      if (filters.type != '' && filters.type !== 'All') {
        filteredData = filteredData.filter(item => item.type == filters.type);
      }
      else {
        filteredData = data.compatibility;
      }

      // Filter the data based on Comp.
      if (filters.comp == 'EU') {
        filteredData = filteredData.filter(item => item.eu == 'Yes');
      }
      else if (filters.comp == 'NA') {
        filteredData = filteredData.filter(item => item.na == 'Yes');
      }
      else {
        filteredData = filteredData;
      }

      // Filter the data based on KW.
      if (filters.kw && filters.kw.length > 0) {
        filteredData = filteredData.filter(item => item.kw >= parseInt(filters.kw[0]) && item.kw <= parseInt(filters.kw[1]));
      }

      // Search the data.
      if (filters.search) {
        filteredData = filteredData.filter(item => item.make.toLowerCase().includes(filters.search.toLowerCase()) || item.model.toLowerCase().includes(filters.search.toLowerCase()));
      }

      setDataCompatibility(filteredData);
    }
  }, [filters]);

  useEffect(() => {
    if (isReady && dataCompatibility) {
      setDataCompatibility([]);
      let sortedData = dataCompatibility;
      // Sort data.
      if (sortList == 'make') {
        sortedData = dataCompatibility.sort((a, b) => a.make.localeCompare(b.make));
      }
      else if (sortList == 'type') {
        sortedData = dataCompatibility.sort((a, b) => a.type.localeCompare(b.type));
      }
      else if (sortList == 'kw') {
        sortedData =  dataCompatibility.sort((a, b) => a.kw - b.kw);
      }
      setDataCompatibility([...sortedData]);
    }

  }, [sortList]);

  const scrollTop = () => {
    // Scroll to top of the app. 
    const appDiv = document.getElementById('cp-compatibility-top');
    window.scrollTo({
      top: appDiv.offsetTop + 40,
      behavior: "smooth"
    });
  }

  // Just a wrapper for the state loop.
  const Wrapper = ({ children }) => {
    return <>{ children }</>;
  }

  const showFilters = (e) => {
    e.preventDefault();
    document.querySelector('.filters').classList.toggle('filters-collapsed');
  }

  useEffect(() => {
    // Toggle filters on mobile.
    if (isMobile) {
      document.querySelector('.filters').classList.add('filters-collapsed');
    }
    else {
      if (document.querySelector('.filters') && document.querySelector('.filters').classList.contains('filters-collapsed')) {
        document.querySelector('.filters').classList.remove('filters-collapsed');
      }
    }
  }, [isMobile]);


  return (
    <div className='cp-compatibility-app' id="cp-compatibility-top">
      <>
        <div>
          <div className='filters cp-sidebar'>
            {isMobile && (
              <div className='filter-toggle'>
                <a href='#' className='show-filters' onClick={showFilters}>{Drupal.t('Show filters')}</a>
                <a href='#' className='hide-filters' onClick={showFilters}>{Drupal.t('Hide filters')}</a>
              </div>
            )}

            <div className='filters-sort cp-sidebar-inner'>
              <SortList isReady={isReady} setSortList={setSortList} />
              
              <Filters dataFilters={data.filters}  setFilters={setFilters} filters={filters} isReady={isReady} />
              <Search setSearch={setSearch} filters={filters} isReady={isReady} setFilters={setFilters} />
            </div>
          </div>
        </div>

        <div className='list'>
          {loading && <div><LoadingAnimation /></div>}

          {(!loading && dataCompatibility && dataCompatibility.length > 0) && (
            <table role="table">
              <thead role="rowgroup">
                <tr role="row">
                  <th role="columnheader">{Drupal.t('Make')}</th>
                  <th role="columnheader">{Drupal.t('Model')}</th>
                  <th role="columnheader">{Drupal.t('Type')}</th>
                  <th role="columnheader">{Drupal.t('Max kW')}</th>
                  {/* <th role="columnheader">{Drupal.t('CP Compatible EU')}</th>
                  <th role="columnheader">{Drupal.t('CP Compatible NA')}</th> */}
                </tr>
              </thead>
              <tbody role="rowgroup">
                {dataCompatibility.map((item, i) => (
                  <tr role="row" key={i} className={`${i % 2 === 0 ? 'even' : 'odd'}`}>
                    <td role="cell">{item.make}</td>
                    <td role="cell">{item.model}</td>
                    <td role="cell">{item.type}</td>
                    <td role="cell">{item.kw}</td>
                    {/* <td role="cell">{item.eu}</td>
                    <td role="cell">{item.na}</td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* No results message. */}
          {(!loading && dataCompatibility && dataCompatibility.length === 0) && (
            <div className="no-results h3--centered">
            <div>
            <h3>{Drupal.t('Sorry, I couldn\'t fetch any hardware. Try another filters instead.')}</h3>
            </div>
            <img alt='No results' className='img-responsive hidden-xs mt40' src='/sites/default/files/inline-images/zero-results-eddie-desktop.png' loading='lazy'></img>
            </div>
          )}

          {/* Pagination */}
          {(!loading && data.pagination && data.pagination.pages > 1) && (
            <Pagination pagination={data.pagination} setFilters={setFilters} filters={filters} isReady={isReady} scrollTop={scrollTop} />
          )}

        </div>
      </>
    </div>
  );
};


const appRoot = document.getElementById('cp-app-compatibility');
if (appRoot) {
  const root = ReactDOM.createRoot(appRoot);
  root.render(
    <>
      <CompatibilityApp />
    </>
  );
};